import { useState, type ReactNode } from "react";
import { Download, ExternalLink } from "lucide-react";
import { extractErrorMessage } from "@/api/client";
import {
  prequalificationApi,
  type PrequalificationDocType,
} from "@/api/prequalification";
import type {
  BuyerPrequalCallEmailPreviewDto,
  BuyerPrequalCallReviewDto,
  BuyerPrequalCallReviewStatus,
} from "@/api/prequalCall";
import { downloadBlob } from "@/api/reports";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { ResourceDrawer } from "@/components/portfolioAdmin/loanDetail/common/ResourceDrawer";
import {
  Button,
  Skeleton,
  StatusBadge,
  useToast,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { PREQUAL_PROGRAM_TERMS } from "@/portal/pages/prequalify/prequalSizing";
import { useDateFormatter } from "@/utils/datetime";
import { DOC_LABEL } from "@/pages/dealFlow/PrequalificationDetailPage";
import { outcomeLabel } from "@/pages/dealFlow/PrequalificationListPage";
import { InlineQueryError } from "../queryStates";
import {
  usePrequalCallReview,
  useSendPrequalCall,
  useWithholdPrequalCall,
} from "./usePrequalCallReview";

/**
 * Review, then send (ruling D10) — the screen between a prequalification taken
 * on a call and anything reaching the buyer.
 *
 * <p>Running the prequalification on the call generates the documents and
 * emails nothing. This drawer shows what was produced — the result, every
 * document, and the email exactly as the server composed it — and holds the
 * only controls that send.</p>
 *
 * <p>🔴 <b>The actions follow the EMAIL the server composed, not the outcome
 * badge.</b> A pass sends the combined education + prequalification email; a
 * decline sends nothing on its own — the consultant chooses "Send decline
 * documents" or "Don't send", and both are recorded. Each decline action states
 * in its confirmation exactly what it will do, because a confirmation that
 * describes a different action than the button performs is worse than none.</p>
 *
 * <p>Once decided, the email is gone from the read and the drawer says what was
 * decided, by whom and when. Nothing here can send twice.</p>
 */
export function PrequalCallReview({
  buyerId,
  submissionId,
  open,
  onClose,
}: {
  buyerId: string;
  submissionId: string;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const review = usePrequalCallReview(buyerId, submissionId, open);
  const send = useSendPrequalCall(buyerId, submissionId);
  const withhold = useWithholdPrequalCall(buyerId, submissionId);
  const [confirming, setConfirming] = useState<PendingConfirm>(null);
  const pending = send.isPending || withhold.isPending;

  const r = review.data;
  // Only an undecided review carries actions; the server drops the email once
  // a decision is made, and this keeps a stale read from offering one anyway.
  const email = r?.reviewStatus === "awaitingReview" ? r.email ?? null : null;

  const doSend = () => {
    setConfirming(null);
    send.mutate(undefined, {
      onSuccess: (result) => toast.success(`Sent to ${result.toEmail}.`),
      onError: (e) => toast.error(extractErrorMessage(e)),
    });
  };

  const doWithhold = () => {
    setConfirming(null);
    withhold.mutate(undefined, {
      onSuccess: () => toast.success("Recorded: the decline documents were not sent."),
      onError: (e) => toast.error(extractErrorMessage(e)),
    });
  };

  return (
    <>
      <ResourceDrawer
        open={open}
        onClose={onClose}
        title="Review and send"
        subtitle={r?.referenceNumber ? `Prequalification ${r.referenceNumber}` : undefined}
        width="lg"
        testId="prequal-call-review"
      >
        {review.isError ? (
          <InlineQueryError
            what="the prequalification review"
            onRetry={() => void review.refetch()}
            retrying={review.isFetching}
          />
        ) : r === undefined ? (
          <Skeleton className="h-48 w-full" />
        ) : (
          <div className="grid gap-space-5">
            <ResultSection review={r} />
            <DocumentsSection submissionId={submissionId} review={r} />
            {r.reviewStatus !== "awaitingReview" ? (
              <DecisionSection review={r} />
            ) : email ? (
              <>
                <EmailSection email={email} />
                <Actions
                  email={email}
                  pending={pending}
                  onSend={doSend}
                  onAskSendDecline={() => setConfirming("sendDecline")}
                  onAskWithhold={() => setConfirming("withhold")}
                />
              </>
            ) : null}
          </div>
        )}
      </ResourceDrawer>

      {/* Siblings of the drawer, not children: a modal mounted inside it would
          bubble its key events through the drawer's own focus trap, and two
          traps do not compose. */}
      {open && email && (
        <>
          <ConfirmModal
            open={confirming === "sendDecline"}
            title="Send the decline documents?"
            message={
              <span data-testid="prequal-call-confirm-send-decline">
                {sendDeclineSentence(email)}
              </span>
            }
            confirmLabel="Email the buyer"
            onConfirm={doSend}
            onCancel={() => setConfirming(null)}
          />
          <ConfirmModal
            open={confirming === "withhold"}
            title="Don't send the decline documents?"
            message={
              <span data-testid="prequal-call-confirm-withhold">{withholdSentence(email)}</span>
            }
            confirmLabel="Record — not sent"
            onConfirm={doWithhold}
            onCancel={() => setConfirming(null)}
          />
        </>
      )}
    </>
  );
}

type PendingConfirm = "sendDecline" | "withhold" | null;

// --- the result ------------------------------------------------------------

/** Money to the cent — a staff screen (ruling Q1). */
function moneyCents(amount: number): string {
  return formatCurrency(amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ResultSection({ review: r }: { review: BuyerPrequalCallReviewDto }) {
  const { date } = useDateFormatter();
  const pass = r.outcome === "Pass";
  const internalSchedules =
    r.documentPath === "InternalBuyer" && r.schedules && r.schedules.length > 0
      ? [...r.schedules].sort((a, b) => b.amortizationMonths - a.amortizationMonths)
      : null;
  return (
    <Section title="Result" testId="prequal-call-result">
      <div className="flex flex-wrap items-center gap-space-3">
        <StatusBadge tone={pass ? "ok" : "warn"}>{outcomeLabel(r.outcome)}</StatusBadge>
        {pass && internalSchedules ? (
          // prequal-v7, internal arm (M78, D5): the business sizes the deal on
          // both payment schedules, so the drawer names both, 20-year first.
          // Staff-only; the 1.35x origination flag lives on the detail page (D2).
          <>
            {internalSchedules.map((s) => (
              <span
                key={s.amortizationMonths}
                className="text-small text-onyx"
                data-testid={`prequal-call-schedule-${s.amortizationMonths}`}
              >
                {s.amortizationMonths / 12}-year schedule:{" "}
                {s.belowMinimum ? (
                  <>Below the {formatCurrency(PREQUAL_PROGRAM_TERMS.minAmountFinanced)} program minimum</>
                ) : (
                  <strong>{moneyCents(s.purchaseFigure ?? s.maximumFinancedAmount)}</strong>
                )}
              </span>
            ))}
          </>
        ) : (
          pass &&
          r.qualifiedAmount != null && (
            <span className="text-small text-onyx" data-testid="prequal-call-qualified">
              {/* The label the letter's headline carries (ruling Q9). A pre-v7
                  internal row keeps the name it was given then. */}
              {r.documentPath === "InternalBuyer" ? "Qualified purchase price" : "Qualified buying power"}
              {": "}
              <strong>{formatCurrency(r.qualifiedAmount)}</strong>
            </span>
          )
        )}
      </div>
      {pass && internalSchedules && (
        <p className="m-0 text-caption text-gray-mid" data-testid="prequal-call-schedule-caption">
          Maximum Financed Amount on each payment schedule, plus the stated down payment (
          {formatCurrency(
            (internalSchedules[0].purchaseFigure ?? internalSchedules[0].maximumFinancedAmount) -
              internalSchedules[0].maximumFinancedAmount,
          )}
          ).
        </p>
      )}
      {(r.referenceNumber || r.validThroughUtc) && (
        <p className="m-0 text-caption text-gray-mid">
          {r.referenceNumber && <>Reference {r.referenceNumber}</>}
          {r.referenceNumber && r.validThroughUtc && " · "}
          {r.validThroughUtc && <>Valid through {date(r.validThroughUtc)}</>}
        </p>
      )}
    </Section>
  );
}

// --- the documents ---------------------------------------------------------

function DocumentsSection({
  submissionId,
  review: r,
}: {
  submissionId: string;
  review: BuyerPrequalCallReviewDto;
}) {
  const toast = useToast();
  const { date } = useDateFormatter();
  const [docPendingId, setDocPendingId] = useState<string | null>(null);

  // The same reads the prequalification detail page uses: a fresh short-lived
  // link per click (never cached — it expires), and the Word copy converted
  // from the exact HTML the PDF was rendered from.
  const openDocument = async (docId: string, download = false) => {
    setDocPendingId(docId);
    try {
      const link = await prequalificationApi.documentLink(submissionId, docId);
      if (download) {
        const a = document.createElement("a");
        a.href = link.url;
        a.download = "";
        a.rel = "noopener";
        a.click();
      } else {
        window.open(link.url, "_blank", "noopener");
      }
    } catch (e) {
      toast.error(extractErrorMessage(e));
    } finally {
      setDocPendingId(null);
    }
  };

  const downloadWord = async (docId: string, docType: PrequalificationDocType) => {
    setDocPendingId(docId);
    try {
      const blob = await prequalificationApi.documentWord(submissionId, docId);
      const reference = r.referenceNumber?.trim();
      downloadBlob(blob, `${DOC_LABEL[docType]}${reference ? ` ${reference}` : ""}.docx`);
    } catch (e) {
      toast.error(extractErrorMessage(e));
    } finally {
      setDocPendingId(null);
    }
  };

  return (
    <Section title="Documents" testId="prequal-call-documents">
      {r.documents.length === 0 ? (
        <p className="m-0 text-small text-gray-mid italic">No documents generated.</p>
      ) : (
        <ul className="m-0 grid gap-space-2 p-0 list-none">
          {r.documents.map((doc) => (
            <li
              key={doc.id}
              className="flex flex-wrap items-center justify-between gap-space-2"
              data-testid="prequal-call-document"
            >
              <div>
                <div className="text-small font-semibold text-onyx">
                  {DOC_LABEL[doc.docType]}
                  {doc.isSuperseded && (
                    <span className="ml-space-2 text-caption font-normal text-gray-mid">
                      superseded
                    </span>
                  )}
                </div>
                <div className="text-caption text-gray-mid">{date(doc.generatedAtUtc)}</div>
              </div>
              <div className="flex gap-space-1">
                <Button
                  variant="tertiary"
                  size="sm"
                  disabled={docPendingId === doc.id}
                  onClick={() => void openDocument(doc.id)}
                  aria-label={`View ${DOC_LABEL[doc.docType]}`}
                >
                  <ExternalLink size={14} strokeWidth={1.5} aria-hidden /> View
                </Button>
                <Button
                  variant="tertiary"
                  size="sm"
                  disabled={docPendingId === doc.id}
                  onClick={() => void openDocument(doc.id, true)}
                  aria-label={`Download ${DOC_LABEL[doc.docType]}`}
                >
                  <Download size={14} strokeWidth={1.5} aria-hidden />
                </Button>
                {doc.hasWordCopy && (
                  <Button
                    variant="tertiary"
                    size="sm"
                    disabled={docPendingId === doc.id}
                    onClick={() => void downloadWord(doc.id, doc.docType)}
                    aria-label={`Download Word copy of ${DOC_LABEL[doc.docType]}`}
                  >
                    Download Word
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// --- the email -------------------------------------------------------------

function EmailSection({ email }: { email: BuyerPrequalCallEmailPreviewDto }) {
  return (
    <Section
      title={email.kind === "decline" ? "The decline email" : "The email"}
      testId="prequal-call-email"
    >
      <p className="m-0 text-small text-onyx" data-testid="prequal-call-email-to">
        Phoenix will send this to{" "}
        {email.toName ? <strong>{email.toName}</strong> : null}
        {email.toName ? " at " : null}
        <strong>{email.toEmail}</strong>.
      </p>

      <LabelledBlock label="Subject">
        <p className="m-0 text-small text-onyx" data-testid="prequal-call-email-subject">
          {email.subject}
        </p>
      </LabelledBlock>

      <LabelledBlock label="Message">
        <div className="max-h-96 overflow-y-auto rounded-md border border-border-light bg-white-warm px-space-3 py-space-2">
          <p
            className="m-0 text-small text-onyx whitespace-pre-wrap"
            data-testid="prequal-call-email-body"
          >
            {email.body}
          </p>
        </div>
      </LabelledBlock>

      <LabelledBlock label={email.attachments.length === 1 ? "Attachment" : "Attachments"}>
        {email.attachments.length === 0 ? (
          <p className="m-0 text-small text-gray-mid" data-testid="prequal-call-email-no-attachments">
            No attachments.
          </p>
        ) : (
          <ul className="m-0 grid gap-space-1 p-0 list-none" data-testid="prequal-call-email-attachments">
            {email.attachments.map((a) => (
              <li key={a.fileName} className="text-small text-onyx">
                {a.fileName}
                {typeof a.sizeBytes === "number" && a.sizeBytes > 0 ? (
                  <span className="text-gray-mid"> · {formatSize(a.sizeBytes)}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </LabelledBlock>

      {email.kind === "educationPrequal" && (
        <p className="m-0 text-caption text-gray-mid">
          Your sign-off and Phoenix&apos;s role statement are added to the message when it
          sends. Sending marks the education email as sent on this buyer and logs it on
          their Communications tab.
        </p>
      )}
    </Section>
  );
}

function Actions({
  email,
  pending,
  onSend,
  onAskSendDecline,
  onAskWithhold,
}: {
  email: BuyerPrequalCallEmailPreviewDto;
  pending: boolean;
  onSend: () => void;
  onAskSendDecline: () => void;
  onAskWithhold: () => void;
}) {
  return (
    <div className="grid gap-space-2" data-testid="prequal-call-actions">
      {/* Text beside the disabled button, never a tooltip: the reason is the only
          thing that tells the consultant what to fix. */}
      {!email.canSend && email.blockedReason && (
        <p className="m-0 text-small text-status-error" data-testid="prequal-call-blocked">
          {email.blockedReason}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-end gap-space-3">
        {email.kind === "decline" ? (
          <>
            <Button
              variant="secondary"
              onClick={onAskWithhold}
              disabled={pending}
              data-testid="prequal-call-withhold"
            >
              Don&apos;t send
            </Button>
            <Button
              onClick={onAskSendDecline}
              disabled={pending || !email.canSend}
              data-testid="prequal-call-send-decline"
            >
              Send decline documents
            </Button>
          </>
        ) : (
          <Button
            onClick={onSend}
            disabled={pending || !email.canSend}
            data-testid="prequal-call-send"
          >
            {pending ? "Sending…" : "Send"}
          </Button>
        )}
      </div>
    </div>
  );
}

/** What "Send decline documents" does, in one sentence composed from what will
 *  actually be attached. */
export function sendDeclineSentence(email: BuyerPrequalCallEmailPreviewDto): string {
  const who = email.toName ? `${email.toName} at ${email.toEmail}` : email.toEmail;
  const names = email.attachments.map((a) => a.fileName);
  const attached =
    names.length === 0
      ? "with no attachments"
      : `with ${joinNames(names)} attached`;
  return `Phoenix emails the decline email to ${who}, ${attached}. `
    + "The send is recorded on the prequalification.";
}

/** What "Don't send" does. */
export function withholdSentence(email: BuyerPrequalCallEmailPreviewDto): string {
  const who = email.toName ?? email.toEmail;
  return `Nothing is emailed to ${who}. The prequalification records that the decline `
    + "documents were not sent, and the documents stay on file.";
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

// --- the decision ----------------------------------------------------------

const DECISION_TEXT: Record<Exclude<BuyerPrequalCallReviewStatus, "awaitingReview">, string> = {
  sent: "Sent to the buyer",
  declineSent: "Decline documents sent to the buyer",
  withheld: "Not sent — the decline documents were not emailed",
};

function DecisionSection({ review: r }: { review: BuyerPrequalCallReviewDto }) {
  const { dateTime } = useDateFormatter();
  if (r.reviewStatus === "awaitingReview") return null;
  // `decidedAtUtc` is a clock stamp — the viewer's zone, never UTC.
  const when = r.decidedAtUtc ? dateTime(r.decidedAtUtc) : "";
  const by = r.decidedByName?.trim();
  const detail = [by ? `by ${by}` : "", when].filter(Boolean).join(", ");
  return (
    <Section title="Decision" testId="prequal-call-decision">
      <p className="m-0 text-small text-onyx">
        <strong>{DECISION_TEXT[r.reviewStatus]}</strong>
        {detail && <span className="text-gray-mid"> — {detail}</span>}
      </p>
    </Section>
  );
}

// --- layout bits -----------------------------------------------------------

function Section({
  title,
  testId,
  children,
}: {
  title: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <section className="grid gap-space-3" data-testid={testId}>
      <h3 className="m-0 font-display font-semibold text-lead text-onyx">{title}</h3>
      {children}
    </section>
  );
}

function LabelledBlock({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-space-1">
      <span className="text-caption text-gray-mid uppercase tracking-wider">{label}</span>
      {children}
    </div>
  );
}

/** Whole KB below a megabyte, one decimal above — the send preview's rule, so a
 *  file reads the same size here as it does in every other buyer send. */
function formatSize(bytes: number): string {
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.max(1, Math.round(kb))} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}
